import React, {
    forwardRef,
    OptionHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
} from 'react';
import cn from 'classnames';

export interface OptionProps extends OptionHTMLAttributes<HTMLOptionElement> {
    /**
     * Content of the option
     */
    name: string;
}

export interface DropdownProps extends SelectHTMLAttributes<HTMLSelectElement> {
    id: string;
    disabled?: boolean;
    /**
     * Label inside the dropdown, can also contain React elements
     */
    label?: ReactNode;
    /**
     * Indicates that the width of the dropdown should by dynamic based on the longest option
     */
    isDynamicWidth?: boolean;
    options?: OptionProps[];
}

/**
 * Representation of a dropdown field
 */
export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
    (props, ref) => {
        const {
            id,
            disabled,
            label,
            isDynamicWidth,
            options = [],
            className,
            style,
            ...rest
        } = props;

        const classes = cn(
            'a-dropdown',
            {
                'a-dropdown--dynamic-width': isDynamicWidth,
                'a-dropdown--disabled': disabled,
            },
            className,
        );

        /*
         * This is a fix for a bug in Firefox, where an onChange receives an old value
         * when there is an upper onMouseDown/Up handler.
         * See https://github.com/facebook/react/issues/12584
         */
        const handleClick = (event: React.MouseEvent) => {
            event.stopPropagation();
        };

        return (
            <div className={classes} style={style}>
                {label && <label htmlFor={id}>{label}</label>}
                <select
                    id={id}
                    ref={ref}
                    disabled={disabled}
                    onMouseDown={handleClick}
                    onMouseUp={handleClick}
                    {...rest}
                >
                    {options.map(option => {
                        const { name, value, ...rest } = option;
                        return (
                            <option
                                key={`option-${value}`}
                                value={value}
                                {...rest}
                            >
                                {name}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    },
);
Dropdown.displayName = 'Dropdown';
